import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import "./Table.css";

/**
 * Reusable Table Component with Sorting
 * @param {Object[]} cols - Array of column definitions { title, render, key, sortValue, fixedStart }.
 *   Set `fixedStart: true` on a column to pin it to the left while scrolling (need not be leading/adjacent columns;
 *   when any column sets it, `fixedStartCols` is ignored).
 * @param {Object[]} data - Array of data objects to render rows.
 * @param {boolean} bordered - Whether the table should have borders.
 * @param {boolean} hoverable - Whether rows should have hover effects.
 * @param {boolean} striped - Whether rows should alternate background colors.
 * @param {boolean} isDark - Whether the table should have a dark theme.
 * @param {Function} getRowClassName - Optional function to get custom className for each row.
 * @param {number} fixedStartCols - Number of leading columns to pin while horizontally scrolling. Default 0 (off, current behavior).
 * @param {number} fixedEndCols - Number of trailing columns to pin while horizontally scrolling. Default 0 (off, current behavior).
 */
const Table = ({
  cols,
  data,
  bordered = true,
  hoverable = false,
  striped = true,
  isDark = false,
  getRowClassName,
  fixedStartCols = 0,
  fixedEndCols = 0,
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [sortedData, setSortedData] = useState(data);
  const tableRef = useRef(null);
  const [stickyOffsets, setStickyOffsets] = useState({ start: [], end: [] });

  const applySort = (rows, key, direction) => {
    const column = cols.find((col) => col && col.key === key);

    if (!column || !key || !direction) {
      return rows;
    }

    const sortFn = column?.sortValue || ((row) => row[key]);

    return [...rows].sort((a, b) => {
      const aVal = sortFn(a);
      const bVal = sortFn(b);

      if (aVal < bVal) return direction === "ascending" ? -1 : 1;
      if (aVal > bVal) return direction === "ascending" ? 1 : -1;
      return 0;
    });
  };

  const handleSort = (key) => {
    if (!Array.isArray(cols)) {
      console.error("cols is not an array:", cols);
      return;
    }

    const column = cols.find((col) => col && col.key === key); // modified this line

    if (!column || !key) {
      return;
    }

    const direction =
      sortConfig.key === key && sortConfig.direction === "ascending"
        ? "descending"
        : "ascending";

    setSortConfig({ key, direction });
    setSortedData(applySort(data, key, direction));
  };

  // Sync data with sortedData on data change
  useEffect(() => {
    if (!sortConfig.key) {
      setSortedData(data);
    } else {
      setSortedData(applySort(data, sortConfig.key, sortConfig.direction));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Indices of columns pinned to the start: explicit `fixedStart` flags win over the leading-count prop.
  const flaggedStartIndexes = cols.reduce((acc, col, i) => (col && col.fixedStart ? [...acc, i] : acc), []);
  const startIndexes = flaggedStartIndexes.length
    ? flaggedStartIndexes
    : Array.from({ length: Math.min(fixedStartCols, cols.length) }, (_, i) => i);
  const startIndexesKey = startIndexes.join(",");

  const hasFixedCols = startIndexes.length > 0 || fixedEndCols > 0;

  // Measure the rendered header cell widths so pinned columns can be offset
  // by their neighbours' actual widths (columns are rarely a fixed size).
  const measureStickyOffsets = useCallback(() => {
    if (!hasFixedCols) return;
    const headerCells = tableRef.current?.querySelectorAll("thead th");
    if (!headerCells || !headerCells.length) return;

    const start = [];
    let acc = 0;
    startIndexesKey.split(",").filter(Boolean).map(Number).forEach((colIndex) => {
      if (colIndex >= headerCells.length) return;
      start[colIndex] = acc;
      acc += headerCells[colIndex].offsetWidth;
    });

    const end = [];
    acc = 0;
    for (let i = 0; i < fixedEndCols && i < headerCells.length; i++) {
      const colIndex = headerCells.length - 1 - i;
      end[colIndex] = acc;
      acc += headerCells[colIndex].offsetWidth;
    }

    setStickyOffsets({ start, end });
  }, [hasFixedCols, startIndexesKey, fixedEndCols]);

  useEffect(() => {
    if (!hasFixedCols) return undefined;
    // Defer to the next frame so header cells have their final layout width.
    const raf = requestAnimationFrame(measureStickyOffsets);
    window.addEventListener("resize", measureStickyOffsets);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", measureStickyOffsets);
    };
  }, [hasFixedCols, measureStickyOffsets, cols, sortedData]);

  const getStickyProps = (colIndex) => {
    if (!hasFixedCols) return null;
    const totalCols = cols.length;

    if (startIndexes.includes(colIndex)) {
      const isEdge = colIndex === startIndexes[startIndexes.length - 1];
      return {
        style: { position: "sticky", left: stickyOffsets.start[colIndex] ?? 0, zIndex: 2 },
        className: isEdge ? "sticky-col sticky-col-start-edge" : "sticky-col",
      };
    }

    if (fixedEndCols && colIndex >= totalCols - fixedEndCols) {
      const isEdge = colIndex === totalCols - fixedEndCols;
      return {
        style: { position: "sticky", right: stickyOffsets.end[colIndex] ?? 0, zIndex: 2 },
        className: isEdge ? "sticky-col sticky-col-end-edge" : "sticky-col",
      };
    }

    return null;
  };

  return (
    <div className="table-responsive app-data-table" style={{ overflow: "auto" }}>
      <table
        ref={tableRef}
        className={`table ${bordered ? "table-bordered" : "table-borderless"}
        ${hoverable ? "table-hover" : ""}
        ${striped ? "table-striped" : ""}
        ${isDark ? "table-dark" : ""}`}
      >
        <thead>
          <tr>
            {cols.map((headerItem, index) => {
              const sticky = getStickyProps(index);
              return (
                <th
                  key={index}
                  className={`text-uppercase${headerItem.className ? ` ${headerItem.className}` : ""}${sticky ? ` ${sticky.className}` : ""}`}
                  style={{ cursor: headerItem.key ? "pointer" : "default", ...(sticky?.style || {}) }}
                  onClick={() => headerItem.key && handleSort(headerItem.key)}
                  role="columnheader"
                  aria-sort={
                    sortConfig.key === headerItem.key
                      ? sortConfig.direction === "ascending"
                        ? "ascending"
                        : "descending"
                      : "none"
                  }
                >
                  {typeof headerItem.title === "string"
                    ? headerItem.title
                    : headerItem.title()}
                  {sortConfig.key === headerItem.key &&
                    (sortConfig.direction === "ascending" ? "🔼" : "🔽")}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedData.length === 0 ? (
            <tr>
              <td colSpan={cols.length} className="text-center">
                No data available
              </td>
            </tr>
          ) : (
            sortedData.map((item, rowIndex) => {
              const baseClassName = item.is_read === 0 ? "unread-row" : "read-row";
              const customClassName = getRowClassName ? getRowClassName(item) : "";
              const className = customClassName || baseClassName;

              return (
                <tr key={rowIndex} className={className}>
                  {cols.map((col, colIndex) => {
                    const sticky = getStickyProps(colIndex);
                    return (
                      <td
                        key={colIndex}
                        className={`${col.className || ""}${sticky ? ` ${sticky.className}` : ""}`}
                        style={sticky?.style}
                      >
                        {col.render(item, rowIndex)}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

Table.propTypes = {
  cols: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
        .isRequired,
      render: PropTypes.func.isRequired,
      key: PropTypes.string, // Used for sorting
      sortValue: PropTypes.func, // Optional custom sort accessor
    })
  ).isRequired,
  data: PropTypes.array.isRequired,
  bordered: PropTypes.bool,
  hoverable: PropTypes.bool,
  striped: PropTypes.bool,
  isDark: PropTypes.bool,
  getRowClassName: PropTypes.func,
  fixedStartCols: PropTypes.number,
  fixedEndCols: PropTypes.number,
};

export default Table;
